import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import {
  LayoutDashboard, Server, Plus, Puzzle, CreditCard, FileText, Coins,
  BarChart3, Settings, HelpCircle, LogOut, ChevronRight, Menu, X, PenSquare,
  Shield, Activity, Wrench, Users, DollarSign
} from 'lucide-react';
import { useState } from 'react';
import NotificationBell from '@/components/notifications/NotificationBell';

const userLinks = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Overview', exact: true },
  { to: '/dashboard/instances', icon: Server, label: 'Instances' },
  { to: '/dashboard/instances/create', icon: Plus, label: 'Create Instance' },
  { to: '/dashboard/skills', icon: Puzzle, label: 'Skills Marketplace' },
  { divider: true },
  { to: '/dashboard/billing', icon: CreditCard, label: 'Billing' },
  { to: '/dashboard/billing/invoices', icon: FileText, label: 'Invoices' },
  { to: '/dashboard/billing/credits', icon: Coins, label: 'Credits' },
  { divider: true },
  { to: '/dashboard/settings', icon: Settings, label: 'Settings' },
  { to: '/dashboard/support', icon: HelpCircle, label: 'Support' },
];

const adminLinks = [
  { header: 'Admin Panel', icon: Shield },
  { to: '/dashboard/admin', icon: Users, label: 'Admin Overview', exact: true },
  { to: '/dashboard/admin/users', icon: Users, label: 'User Management' },
  { to: '/dashboard/admin/instances', icon: Server, label: 'All Instances' },
  { to: '/dashboard/admin/blog', icon: PenSquare, label: 'Blog Manager' },
  { to: '/dashboard/admin/status', icon: Activity, label: 'System Status' },
  { to: '/dashboard/admin/config', icon: Wrench, label: 'Platform Config' },
  { to: '/dashboard/admin/pricing', icon: DollarSign, label: 'Pricing & Trials' },
  { to: '/dashboard/admin/analytics', icon: BarChart3, label: 'Analytics' },
];

export default function DashboardLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isSuperuser = user?.is_superuser || user?.is_admin || user?.is_staff;

  const isActive = (to, exact) => exact ? location.pathname === to : location.pathname.startsWith(to) && (to !== '/dashboard' || location.pathname === '/dashboard');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const renderLinks = (links) => links.map((item, i) => {
    if (item.divider) return <div key={i} className="my-3 mx-2 border-t border-zinc-800" />;
    if (item.header) {
      return (
        <div key={i} className="flex items-center gap-2 px-3 py-2 mt-4 mb-1">
          <item.icon size={14} className="text-amber-500" />
          <span className="text-xs font-mono text-amber-500 uppercase tracking-wider">{item.header}</span>
        </div>
      );
    }
    const Icon = item.icon;
    const active = isActive(item.to, item.exact);
    return (
      <Link
        key={item.to}
        to={item.to}
        data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-mono transition-all ${active ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-zinc-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent'}`}
      >
        <Icon size={16} />
        <span>{item.label}</span>
        {active && <ChevronRight size={14} className="ml-auto" />}
      </Link>
    );
  });

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-zinc-800">
        <Link to="/" className="text-emerald-500 font-mono font-bold text-base">
          <span className="text-cyan-400">{'>'}</span>_ClawStinger
        </Link>
      </div>

      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
        {renderLinks(userLinks)}
        {isSuperuser && renderLinks(adminLinks)}
      </nav>

      <div className="p-4 border-t border-zinc-800">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center font-mono text-sm font-bold ${isSuperuser ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
            {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm text-white font-mono truncate">{user?.full_name || 'User'}</p>
              {isSuperuser && <Shield size={12} className="text-amber-500" />}
            </div>
            <p className="text-xs text-zinc-500 truncate">{user?.email || 'user@email.com'}</p>
          </div>
        </div>
        <button
          data-testid="sidebar-logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm font-mono text-zinc-500 hover:text-red-400 hover:bg-red-400/5 rounded-sm transition-all"
        >
          <LogOut size={14} />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="dashboard-layout" className="flex h-screen bg-[#050505]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-[#080808] border-r border-zinc-800 flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-64 bg-[#080808] border-r border-zinc-800 z-10 animate-slide-in-left">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 border-b border-zinc-800 flex items-center justify-between px-4 lg:px-6 bg-[#080808] flex-shrink-0">
          <button
            data-testid="mobile-sidebar-toggle"
            className="lg:hidden text-zinc-400 hover:text-white"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-4">
            <span className="text-xs font-mono text-zinc-500">
              Credits: <span className="text-emerald-400">${user?.creditBalance?.toFixed(2) || '0.00'}</span>
            </span>
            <NotificationBell />
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
